"use client";

import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { FAQ_QUESTIONS } from "@/lib/faq";
import type { FaqQuestion } from "@/lib/faq";

// The homepage teaser shows the same content as the full FAQ page, so the
// two never drift apart — see lib/faq.ts.
const FAQS = FAQ_QUESTIONS.slice(0, 6);

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.04 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── FAQ Item ────────────────────────────────────────────────────────────────

function FaqItem({
  faq,
  index,
  isOpen,
  onToggle,
  reduce,
}: {
  faq: FaqQuestion;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  reduce: boolean;
}) {
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;

  return (
    <motion.div
      variants={fadeUp(reduce)}
      className={cn(
        "overflow-hidden rounded-card border bg-white transition-colors",
        isOpen
          ? "border-primary-200 shadow-[0_10px_30px_-18px_rgba(10,45,110,0.18)]"
          : "border-[#e3ecfb]"
      )}
    >
      <button
        id={buttonId}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="flex w-full items-start justify-between gap-6 px-5 py-5 text-left sm:px-6 sm:py-6"
      >
        <span className="text-base font-semibold leading-snug text-primary-900 sm:text-lg">
          {faq.question}
        </span>
        <span
          className={cn(
            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors",
            isOpen
              ? "border-primary-600 bg-primary-600 text-white"
              : "border-[#e3ecfb] bg-[#f7faff] text-primary-700"
          )}
          aria-hidden="true"
        >
          <Plus
            className={cn(
              "h-4 w-4 transition-transform duration-300",
              isOpen ? "rotate-45" : "rotate-0"
            )}
            strokeWidth={2.2}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            key="content"
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            initial={reduce ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduce ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.2, 0, 0, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-6 pr-14 text-base leading-[1.55] text-text-body sm:px-6 sm:pr-16">
              {faq.answer}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

export function FaqSection() {
  const reduce = useReducedMotion() ?? false;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <motion.section
      className="bg-white pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="faq-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={container}
    >
      <div className="mx-auto grid max-w-[1200px] gap-10 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        {/* Header */}
        <motion.div variants={fadeUp(reduce)}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
            Before you start
          </p>
          <h2
            id="faq-heading"
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
          >
            Questions people ask before they start
          </h2>
          <p className="mt-4 max-w-[420px] text-base leading-relaxed text-text-body">
            The essentials on buying, selling, and cashing out with JustGold.
            Still curious? Check the{" "}
            <a
              href="/faqs"
              className="font-semibold text-primary-600 underline decoration-primary-200 underline-offset-4 hover:text-primary-800"
            >
              full FAQ
            </a>
            .
          </p>
        </motion.div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {FAQS.map((faq, i) => (
            <FaqItem
              key={faq.question}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              reduce={reduce}
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
}
